import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, ArrowLeft, AlertCircle, Lock, KeyRound } from 'lucide-react';
import { useAuth } from '../App';
import toast from 'react-hot-toast';
import './Auth.css';
import './AdminLogin.css';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [step, setStep] = useState('credentials');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { loginAdmin, admin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (admin) {
      navigate('/admin');
    }
  }, [admin, navigate]);

  useEffect(() => {
    const gtEl = document.getElementById('google_translate_element');
    if (gtEl) gtEl.style.setProperty('display', 'none', 'important');
    return () => { if (gtEl) gtEl.style.removeProperty('display'); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (step === 'credentials') {
      if (!email.trim() || !password.trim()) {
        setError('Please enter both email and password');
        return;
      }
    } else if (step === '2fa') {
      if (!twoFactorCode.trim() || twoFactorCode.length !== 6) {
        setError('Please enter a valid 6-digit code');
        return;
      }
    }

    setIsLoading(true);

    try {
      const result = await loginAdmin({
        email,
        password,
        twoFactorCode: step === '2fa' ? twoFactorCode : undefined,
      });

      if (result.requires2FA) {
        setStep('2fa');
        setTwoFactorCode('');
        setIsLoading(false);
        return;
      }

      if (result.success) {
        toast.success('Welcome to Admin Panel');
        navigate('/admin');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    }

    setIsLoading(false);
  };

  return (
    <div className="auth-page admin-login-page">
      <div className="admin-login-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="admin-login-card glass-card"
        >
          <Link to="/" className="back-link">
            <ArrowLeft size={18} />
            Back to Home
          </Link>

          <div className="admin-login-header">
            <div className="admin-logo-icon">
              {step === 'credentials' ? (
                <Shield size={32} strokeWidth={2} />
              ) : (
                <KeyRound size={32} strokeWidth={2} />
              )}
            </div>
            <h1>{step === 'credentials' ? 'Admin Panel' : 'Two-Factor Authentication'}</h1>
            <p>
              {step === 'credentials'
                ? 'Sign in to access the administration dashboard'
                : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="admin-login-form">
            {step === 'credentials' && (
              <>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    placeholder="admin@example.com"
                    autoComplete="email"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </div>
              </>
            )}

            {step === '2fa' && (
              <div className="form-group tfa-input-group">
                <label htmlFor="twoFactorCode">Authentication Code</label>
                <input
                  type="text"
                  id="twoFactorCode"
                  value={twoFactorCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setTwoFactorCode(val);
                    setError('');
                  }}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  className="tfa-code-input"
                  autoFocus
                />
              </div>
            )}

            {error && (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  {step === 'credentials' ? 'Signing in...' : 'Verifying...'}
                </>
              ) : (
                <>
                  {step === 'credentials' ? <Lock size={18} /> : <KeyRound size={18} />}
                  {step === 'credentials' ? 'Sign In' : 'Verify Code'}
                </>
              )}
            </button>

            {step === '2fa' && (
              <button
                type="button"
                className="btn btn-ghost w-full"
                onClick={() => { setStep('credentials'); setTwoFactorCode(''); setError(''); }}
                style={{ marginTop: '8px' }}
              >
                <ArrowLeft size={16} />
                Back to credentials
              </button>
            )}
          </form>

          <div className="admin-login-footer">
            <div className="security-badge">
              <Shield size={14} />
              <span>Protected by enterprise-grade security</span>
            </div>
          </div>
        </motion.div>

        <div className="admin-login-hint">
          <p>Authorized personnel only</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
